﻿import DataAppStore from "../../../data/AppStore.json";
import DataChest from "../../../data/Chest.json";
import DataConfig from "../../../data/Config.json";
import DataCreatures from "../../../data/Creatures.json";
import DataEpochs from "../../../data/Epochs.json";
import DataGameplay from "../../../data/Gameplay.json";
import DataInterstitialRewards from "../../../data/InterstitialRewards.json";
import DataMissions from "../../../data/Missions.json";
import DataPlanets from "../../../data/Planets.json";
import DataSlots from "../../../data/Slots.json";
import DataTapChallenge from "../../../data/TapChallenge.json";
import DataUpgrades from "../../../data/Upgrades.json";
import DataWelcomeBack from "../../../data/WelcomeBack.json";

import {GameScreen, MoneyType, RewardType, SlotItem} from "../enums";
import {WeightedRandomization} from "../Utils/Utils";
import {Core} from "../Base/Core";
import {JSONGameData} from "./JSONGameData";
import {Random} from "../Utils/Unity/Random";
import {MonoBehaviour} from "../Utils/Unity/MonoBehaviour";

export class GameData extends MonoBehaviour {

    constructor() {
        super();
        this.Config = new ConfigData();
        this.AppStore = new AppStoreData();
        this.Gameplay = new GameplayData();
        this.Creatures = new CreaturesData();
        this.Epochs = new EpochsData();
        this.Upgrades = new UpgradesData();
        this.Planets = new PlanetsData();
        this.Missions = new MissionsData();
        this.Chest = new ChestData();
        this.Slots = new SlotsData();
        this.InterstitialRewards = new InterstitialRewards();
        this.WelcomeBack = new WelcomeBackSettings();
        this.TapChallenge = new TapChallenge();
    }

    Init() {
        this.Config.Init(DataConfig);
        this.AppStore.Init(DataAppStore);
        this.Gameplay.Init(DataGameplay);
        this.Creatures.Init(DataCreatures);
        this.Epochs.Init(DataEpochs);
        this.Planets.Init(DataPlanets);
        this.Upgrades.Init(DataUpgrades);
        this.Missions.Init(DataMissions);
        this.Chest.Init(DataChest);
        this.Slots.Init(DataSlots);
        this.InterstitialRewards.Init(DataInterstitialRewards);
        this.WelcomeBack.Init(DataWelcomeBack);
        this.TapChallenge.Init(DataTapChallenge);
    }

    GetAppStoreProductByPlatformId(platformId) {
        for (let p of this.AppStore.inAppProducts) {
            if (p.PlatformStoreId === platformId) return p;
        }
        return null;
    }

    GetInGameProductById(id) {
        for (let p of this.AppStore.inGameProducts) {
            if (p.id === id) return p;
        }
        return null;
    }

    GetAppStoreProductById(id) {
        for (let p of this.AppStore.inAppProducts) {
            if (p.id === id) return p;
        }
        return null;
    }

    toJSON() {
        return {
            Config: this.Config.toJSON(),
            AppStore: this.AppStore.toJSON(),
            Gameplay: this.Gameplay.toJSON(),
            Creatures: this.Creatures.toJSON(),
            Epochs: this.Epochs.toJSON(),
            Upgrades: this.Upgrades.toJSON(),
            Planets: this.Planets.toJSON(),
            Missions: this.Missions.toJSON(),
            Chest: this.Chest.toJSON(),
            Slots: this.Slots.toJSON(),
            InterstitialRewards: this.InterstitialRewards.toJSON(),
            WelcomeBack: this.WelcomeBack.toJSON(),
            TapChallenge: this.TapChallenge.toJSON(),
        };
    }
}

export class ConfigData extends JSONGameData {

    Awake() {
        super.Awake();
        this.creatureRankTimeouts = [];
    }

    GetTimeoutByCreature(lastCreatureRank) {
        if (this.creatureRankTimeouts && this.creatureRankTimeouts.length >= 2 && this.creatureRankTimeouts.length % 2 == 0) {
            for (let i = this.creatureRankTimeouts.length - 2; i >= 0; i -= 2) {
                if (this.creatureRankTimeouts[i] <= lastCreatureRank) {
                    return this.creatureRankTimeouts[i + 1];
                }
            }
        }
        return this.defaultTimeout;
    }
}

export class CreaturesData extends JSONGameData {

    Awake() {
        super.Awake();
        this.creatures = []; // CreatureData[]
        this.creaturesDic = new Map(); // Dictionary<int, CreatureData>
    }

    Get(id) {
        return this.creaturesDic.get(id);
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.creatures = this.creatures.map(data => new CreatureData(data));
        for (let creature of this.creatures) {
            this.creaturesDic.set(creature.id, creature);
        }
    }

    toJSON() {
        return {
            creatures: this.creatures.map(obj => obj.toJSON())
        }
    }
}

export class TapChallenge extends JSONGameData {

    Awake() {
        super.Awake();
        this.isActive = false;
        this.duration = 0;
        this.baseClickCount = 0;
        this.reward = new Reward();
        this.additionalTime = 0;
        this.victoryDurationIncrease = 0;
        this.lostDurationDecrease = 0;
        this.maxClickAmount = 0;
        this.rewardMultiplyRate = 0;
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.reward = new Reward(this.reward);
    }
}

export class EpochsData extends JSONGameData {

    Awake() {
        super.Awake();
        this.epochs = []; // EpochData[]
        this.epochById = new Map(); // Dictionary<int, EpochData>
        this.epochByCreature = new Map(); // Dictionary<int, EpochData>
        this.epochsByLevel = new Map(); // Dictionary<int, List<EpochData>>
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.epochs = this.epochs.map(data => new EpochData(data));
        for (let e of this.epochs) {
            this.epochById.set(e.id, e);
            if (!this.epochsByLevel.has(e.epochLevel)) {
                this.epochsByLevel.set(e.epochLevel, []); // new List<EpochData>()
            }
            this.epochsByLevel.get(e.epochLevel).push(e);

            for (let c of e.creatures) {
                this.epochByCreature.set(c, e);
            }

            if (!e.nextEpochs || !e.nextEpochs.length) {
                // in next events this might change. Maybe final creature should be LAST ?
                let finalCreature = Core.GameData.Creatures.Get(e.creatures[0]);
                finalCreature.IsFinalForm = true;
            }
        }

        for (let currentEpoch of this.epochs) {
            if (currentEpoch.epochLevel == 1) continue;
            for (let levelEpoch of this.epochsByLevel.get(currentEpoch.epochLevel - 1)) {
                if (levelEpoch.nextEpochs.includes(currentEpoch.id)) {
                    currentEpoch.PrevEpochId = levelEpoch.id;
                    break;
                }
            }
        }
    }

    toJSON() {
        return {
            epochs: this.epochs.map(obj => obj.toJSON())
        }
    }

    // public bool GetNextCreature(int id, out int nextCreatureId)
    GetNextCreature(id) {
        for (let i = 0; i < this.epochs.length; i++) {
            let e = this.epochs[i];
            for (let j = 0; j < e.creatures.length; j++) {
                if (e.creatures[j] == id) {
                    if (j < e.creatures.length - 1) {
                        return e.creatures[j + 1];
                    } else if (i < epochs.Length - 1) {
                        return this.epochs[i + 1].creatures[0];
                    } else {
                        return -1;
                    }
                }
            }
        }
        return -1;
    }

    GetById(id) {
        return this.epochById.get(id);
    }

    GetByCreatureId(id) {
        return this.epochByCreature.get(id);
    }

    GetEpochsDepth() {
        let depth = 0;
        let epoch = this.epochs[0];
        while (epoch != null) {
            depth++;
            if (epoch.nextEpochs && epoch.nextEpochs.length > 0) {
                epoch = Core.GameData.Epochs.GetById(epoch.nextEpochs[0]);
            } else {
                epoch = null;
            }
        }
        return depth;
    }

    GetEpochsList(withEpochId) {
        let epochsList = []; // new List<int>()
        let depth = this.GetEpochsDepth();
        let epochData = Core.GameData.Epochs.GetById(withEpochId);

        let currEpoch = epochData;
        // add next epochs after current epoch
        while (currEpoch != null) {
            epochsList.push(currEpoch.id);
            if (currEpoch.nextEpochs && currEpoch.nextEpochs.length > 0) {
                currEpoch = Core.GameData.Epochs.GetById(currEpoch.nextEpochs[0]);
            } else {
                currEpoch = null;
            }
        }

        // add prev epochs
        currEpoch = epochData;
        while (currEpoch.PrevEpochId != 0) {
            currEpoch = Core.GameData.Epochs.GetById(currEpoch.PrevEpochId);
            epochsList.unshift(currEpoch.id);
        }

        return epochsList;
    }
}

export class PlanetsData extends JSONGameData {

    Awake() {
        super.Awake();
        this.basicPlanetPriceSoft = 0n;
        this.basicCreaturePriceSoft = 0n;
        this.basicPlanetPriceSoftStr = "0";
        this.basicCreaturePriceSoftStr = "0";
        this.planets = []; // PlanetData[]
        this.planetsDic = new Map(); // Dictionary<int, PlanetData>
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.basicPlanetPriceSoft = BigInt(this.basicPlanetPriceSoftStr);
        this.basicCreaturePriceSoft = BigInt(this.basicCreaturePriceSoftStr);
        this.planets = this.planets.map(data => new PlanetData(data));
        for (let p of this.planets) this.planetsDic.set(p.id, p);
    }

    toJSON() {
        return {
            basicPlanetPriceSoftStr: this.basicPlanetPriceSoft.toString(),
            basicCreaturePriceSoftStr: this.basicCreaturePriceSoft.toString(),
            planets: this.planets.map(obj => obj.toJSON()),
        }
    }

    GetPlanet(id) {
        return this.planetsDic.get(id);
    }
}

export class PlanetData extends JSONGameData {

    Awake() {
        super.Awake();
        this.id = 0;
        this.planetName = "";
        this.evolutionBaseSoft = 0; // float
        this.purchasesBaseSoft = 0; // float
        this.creaturePriceIncreaseHard = 0;
        this.tapIncomeMultiplayer = 0;
        this.eventId = 0;
        this.pricesFactorSoft = 0n;
        this.incomeFactorSoft = 0n;
        this.priceFactorSoftStr = "0";
        this.incomeFactorSoftStr = "0";
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.pricesFactorSoft = BigInt(this.priceFactorSoftStr);
        this.incomeFactorSoft = BigInt(this.incomeFactorSoftStr);
    }

    toJSON() {
        return {
            id: this.id,
            planetName: this.planetName,
            evolutionBaseSoft: this.evolutionBaseSoft,
            purchasesBaseSoft: this.purchasesBaseSoft,
            creaturePriceIncreaseHard: this.creaturePriceIncreaseHard,
            tapIncomeMultiplayer: this.tapIncomeMultiplayer,
            eventId: this.eventId,
            priceFactorSoftStr: this.pricesFactorSoft.toString(),
            incomeFactorSoftStr: this.incomeFactorSoft.toString(),
        }
    }

    GetEvent() {
        return null;
    }

    GetPrice() {
        let price = new Price();
        price.priceType = MoneyType.Soft;
        price.priceValue = Core.GameData.Planets.basicPlanetPriceSoft * this.pricesFactorSoft;
        return price;
    }
}

export class UpgradesData extends JSONGameData {

    Awake() {
        super.Awake();
        this.upgrades = []; // UpgradeData[]
        this.upgradesDic = new Map(); // Dictionary<UpgradeType, UpgradeData>
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.upgrades = this.upgrades.map(data => new UpgradeData(data));
        for (let upgrade of this.upgrades) this.upgradesDic.set(upgrade.upgradeType, upgrade);
    }

    toJSON() {
        return {
            upgrades: this.upgrades.map(obj => obj.toJSON())
        }
    }

    UpgradeByType(type) {
        return this.upgradesDic.get(type);
    }
}

export class UpgradeData extends JSONGameData {

    Awake() {
        super.Awake();
        this.upgradeType = "";
        this.steps = []; // UpgradeStep[]
        this.unlockCreatureId = 0;
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.steps = this.steps.map(data => new UpgradeStep(data));
    }

    toJSON() {
        return {
            upgradeType: this.upgradeType,
            steps: this.steps.map(obj => obj.toJSON()),
            unlockCreatureId: this.unlockCreatureId,
        }
    }

    TotalValue(upgradeIndex) {
        let totalValue = 0, steps = this.steps.slice(0, upgradeIndex + 1);
        for (let step of steps) totalValue += step.value;
        return totalValue;
    }
}

export class UpgradeStep extends JSONGameData {

    Awake() {
        super.Awake();
        this.price = new Price();
        this.value = 0;
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.price = new Price(this.price);
    }

    toJSON() {
        return {
            price: this.price.toJSON(),
            value: this.value,
        }
    }

    GetPrice() {
        let tPrice = new Price();

        tPrice.priceType = this.price.priceType;
        tPrice.priceValue = this.price.priceValue;

        if (tPrice.priceType == MoneyType.Soft) {
            let planet = Core.GameData.Planets.GetPlanet(Core.Player.PlanetsSave.currentPlanetId);
            //var planet = Core.GameData.Planets.GetPlanet(1);
            tPrice.priceValue *= planet.pricesFactorSoft;
        }
        return tPrice;
    }
}

export class EpochData extends JSONGameData {

    get EpochIcon() {
        if (!this.icon) {
            //icon = Resources.Load<Sprite>("EpochIcons/" + id);
        }
        return this.icon;
    }

    Awake() {
        super.Awake();
        this.id = 0;
        this.epochName = "";
        this.nextEpochs = []; // List<int>
        this.creatures = []; // List<int>
        this.epochLevel = 0;
        this.PrevEpochId = 0;
        this.spritesByPlanetsIds = new Map(); // Dictionary<int, Sprite>
        this.icon = null; // Sprite
    }

    fromJSON(json) {
        super.fromJSON(json);
    }

    toJSON() {
        return {
            id: this.id,
            epochName: this.epochName,
            nextEpochs: this.nextEpochs,
            creatures: this.creatures,
            epochLevel: this.epochLevel,
        }
    }

    EpochSprite(planetId) {
        if (this.spritesByPlanetsIds == null) {
            this.spritesByPlanetsIds = new Map(); // new Dictionary<int, Sprite>();
        }
        // TODO: implement this
        // if (!spritesByPlanetsIds.ContainsKey(planetId)) {
        //     spritesByPlanetsIds.Add(planetId, Resources.Load<Sprite>("EpochSprites/" + id));
        // }
        return this.spritesByPlanetsIds.get(planetId);
    }
}

export class CreatureData extends JSONGameData {

    Awake() {
        super.Awake();
        this.id = 0;
        this.creatureName = "";
        this.creatureRank = 0;
        this.basePriceHard = 0;
        this.basicIncomeSoft = 0n;
        this.basicIncomeSoftStr = "0";
        this.IsFinalForm = false;
        this.sprite = null; // Sprite
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.basicIncomeSoft = BigInt(this.basicIncomeSoftStr);
    }

    toJSON() {
        return {
            id: this.id,
            creatureName: this.creatureName,
            creatureRank: this.creatureRank,
            basePriceHard: this.basePriceHard,
            basicIncomeSoftStr: this.basicIncomeSoft.toString(),
        }
    }

    GetSoftPerSecond() { // BigInt
        let planet = Core.GameData.Planets.GetPlanet(Core.Player.PlanetsSave.currentPlanetId);
        let baseValue = this.basicIncomeSoft * planet.incomeFactorSoft;
        if (Core.Store.VipActive) {
            return baseValue * BigInt(Core.GameData.AppStore.vip.softMoneyIncomeBonus);
        }
        return baseValue;
    }
}

export class AppStoreData extends JSONGameData {

    Awake() {
        super.Awake();
        this.inAppProducts = []; // AppStoreProduct[]
        this.inGameProducts = []; // InGameProduct[]
        this.hardAdsBonusReward = new Reward();
        this.boosters = []; // BoosterData[]
        this.vip = new VipData();
        this.boostersDic = new Map(); // Dictionary<BoosterType, BoosterData>
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.inAppProducts = this.inAppProducts.map(data => new AppStoreProduct(data));
        this.inGameProducts = this.inGameProducts.map(data => new InGameProduct(data));
        this.boosters = this.boosters.map(data => new BoosterData(data));
        for (let b of this.boosters) {
            this.boostersDic.set(b.boosterType, b);
        }
        this.hardAdsBonusReward = new Reward(this.hardAdsBonusReward);
        this.vip = new VipData(this.vip);
    }

    toJSON() {
        return {
            inAppProducts: this.inAppProducts.map(obj => obj.toJSON()),
            inGameProducts: this.inGameProducts.map(obj => obj.toJSON()),
            hardAdsBonusReward: this.hardAdsBonusReward.toJSON(),
            boosters: this.boosters.map(obj => obj.toJSON()),
            vip: this.vip.toJSON(),
        }
    }

    GetBooster(type) {
        return this.boostersDic.get(type);
    }

    GetInAppProduct(productId) {
        return this.inAppProducts.find(cfg => cfg.id === productId);
    }

    GetInGameProduct(productId) {
        return this.inGameProducts.find(cfg => cfg.id === productId);
    }
}

export class VipData extends JSONGameData {

    Awake() {
        super.Awake();
        this.hardMoneyBonus = 0; // float
        this.softMoneyIncomeBonus = 0;
        this.hardMoneyDailyBonus = 0;
    }

    toJSON() {
        return {
            hardMoneyBonus: this.hardMoneyBonus,
            softMoneyIncomeBonus: this.softMoneyIncomeBonus,
            hardMoneyDailyBonus: this.hardMoneyDailyBonus,
        }
    }
}

export class BoosterData extends JSONGameData {

    Awake() {
        super.Awake();
        this.boosterType = ""; // BoosterType
        this.price = new Price();
        this.durationSeconds = 0;
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.price = new Price(this.price);
    }

    toJSON() {
        return {
            boosterType: this.boosterType,
            price: this.price.toJSON(),
            durationSeconds: this.durationSeconds,
        }
    }
}

export class Price extends JSONGameData {

    get priceValueStr() {
        return this.priceValue.toString();
    }

    set priceValueStr(val) {
        this.priceValue = BigInt(val || 0);
    }

    Awake() {
        super.Awake();
        this.priceType = ""; // MoneyType
        this.priceValue = 0n;
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.priceValueStr = json.priceValueStr;
    }

    toJSON() {
        return {
            priceType: this.priceType,
            priceValueStr: this.priceValueStr,
        }
    }
}

export class Reward extends JSONGameData {

    get name() {
        switch (this.type) {
            case RewardType.MoneyHard:
                return "Gems";

            case RewardType.MoneySoft:
                return "Coins";

            case RewardType.NoAds:
                return "No ads";

            default:
                return "";
        }
    }

    get realValue() {
        switch (this.type) {
            case RewardType.MoneyHard:
                if (Core.Store.VipActive) {
                    let multiplier = 100; // Needed to avoid BigInt rounding
                    let bonus = BigInt(Core.GameData.AppStore.vip.hardMoneyBonus * multiplier);
                    return (this.value * bonus) / BigInt(multiplier);
                }
                return this.value;

            case RewardType.MoneySoft:
                return this.value;

            case RewardType.NoAds:
                return "";

            case RewardType.MoneySoftBySoftSpeed:
                return this.value * (GameManager.gameController.MoneySoftSpeed || 1n);

            case RewardType.MaxCreature:
                let collectionLength = Core.Player.PlanetsSave.LastNotEventPlanet.collection.length;
                if (collectionLength > 0) {
                    return Core.Player.PlanetsSave.LastNotEventPlanet.collection[collectionLength - 1];
                }
                return 0;

            case RewardType.RandomCreatureFromCurrentEpoch:
                return "";

            case RewardType.BoosterIncomeX4:
                return this.valueInt;

            case RewardType.BoosterIncomeX2:
                return this.valueInt;

            case RewardType.BoosterCreaturesDiscount:
                return this.valueInt;

            case RewardType.Spins:
                return this.valueInt;

            case RewardType.CreatureFromCollectionWithOffset:
                return Core.Player.PlanetsSave.CurrentPlanet.GetCollectionCreatureWithOffset(this.valueInt);

            case RewardType.LastShopCreature:
                let shopCreatures = Core.Player.PlanetsSave.CurrentPlanet.GetAvailableForPurchaseCreatures();
                if (shopCreatures.length > 0) {
                    return shopCreatures[shopCreatures.length - 1];
                }
                return 0;

            case RewardType.FirstCreatureOffer:
                return Core.UI.firstCreatureOffer.RequestedCreatureId;

            case RewardType.SkipEpochsOffer:
                return "";

            case RewardType.SpinsOffer1:
                return "";

            case RewardType.SpinsOffer2:
                return "";
        }
    }

    Awake() {
        super.Awake();
        this.type = ""; // RewardType
        this.value = 0n;
        this.valueStr = "0";
        this.valueInt = 0;
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.value = BigInt(this.valueStr || 0);
    }

    toJSON() {
        return {
            type: this.type,
            valueStr: this.value.toString(),
            valueInt: this.valueInt,
        }
    }

    toString() {
        return `${RewardType[this.type]} ${this.realValue}`.trim();
    }

    Take(item, fromInApp = false) {
        let gameScreen = Core.UI ? Core.UI.GetGameScreen() : GameScreen.empty;
        let {valueInt} = this;

        console.log("TAKE REWARD", this.toString());

        switch (this.type) {
            case RewardType.MoneyHard:
                let value = (fromInApp && Core.Store.VipActive) ? this.realValue : this.value;
                Core.Player.AddMoney(MoneyType.Hard, value, GameCategory.empty, gameScreen, item);
                break;

            case RewardType.MoneySoft:
                Core.Player.AddMoney(MoneyType.Soft, this.value);
                break;

            case RewardType.NoAds:
                Core.Player.NoAds = 1;
                break;

            case RewardType.MoneySoftBySoftSpeed:
                let val = GameManager.gameController.MoneySoftSpeed == 0 ? 1 : GameManager.gameController.MoneySoftSpeed;
                Core.Player.AddMoney(MoneyType.Soft, this.value * val);
                break;

            case RewardType.MaxCreature:
                let collectionLength = Core.Player.PlanetsSave.LastNotEventPlanet.collection.length;
                if (collectionLength > 0) {
                    let creatureId = Core.Player.PlanetsSave.LastNotEventPlanet.collection[collectionLength - 1];
                    Core.Player.PlanetsSave.LastNotEventPlanet.AddCreature(creatureId, true);
                    GameManager.UpdateCurrentPlanet();
                }
                break;

            case RewardType.RandomCreatureFromCurrentEpoch:
                let epochId = GameManager.gameView.selectedEpochId;
                let planet = Core.Player.PlanetsSave.CurrentPlanet;
                let availableCreatures = [];
                for (let c of planet.collection) {
                    let isEpochOkay = Core.GameData.Epochs.GetByCreatureId(c).id === epochId;
                    let isLastCreature = planet.collection[planet.collection.length - 1] == c;
                    if (isEpochOkay) {
                        if (!isLastCreature || availableCreatures.length == 0) {
                            availableCreatures.push(c);
                        }
                    }
                }
                if (availableCreatures.length > 0) {
                    let randomCreature = availableCreatures[~~Random.Range(0, availableCreatures.length - 1)];
                    GameManager.gameController.AddCreatureRandomPosition(randomCreature, false);
                }
                break;

            case RewardType.BoosterIncomeX4:
                Core.Boosters.AddBoosterTime(BoosterType.IncomeX4, valueInt);
                break;

            case RewardType.BoosterIncomeX2:
                Core.Boosters.AddBoosterTime(BoosterType.Income, valueInt);
                break;

            case RewardType.BoosterCreaturesDiscount:
                Core.Boosters.AddBoosterTime(BoosterType.CreaturesDiscount, valueInt);
                break;

            case RewardType.Spins:
                Core.Player.AddMoney(MoneyType.Spins, valueInt);
                break;

            case RewardType.CreatureFromCollectionWithOffset:
                let bonusCreatureId = Core.Player.PlanetsSave.CurrentPlanet.GetCollectionCreatureWithOffset(valueInt);
                GameManager.gameController.AddCreatureRandomPosition(bonusCreatureId, false);
                break;

            case RewardType.LastShopCreature:
                let shopCreatures = Core.Player.PlanetsSave.CurrentPlanet.GetAvailableForPurchaseCreatures();
                if (shopCreatures.length > 0) {
                    let lastCreature = shopCreatures[shopCreatures.length - 1];
                    GameManager.gameController.AddCreatureRandomPosition(lastCreature, false);
                }
                break;

            case RewardType.FirstCreatureOffer:
                for (let i = 0; i < 6; i++) {
                    GameManager.gameController.AddCreatureRandomPosition(Core.UI.firstCreatureOffer.RequestedCreatureId, false);
                }
                Core.Player.Timers.firstCreatureOfferTime = 0;
                break;

            case RewardType.SkipEpochsOffer:
                for (let eId = 1; eId <= 2; eId++) {
                    let epoch = Core.GameData.Epochs.GetById(eId);
                    for (let cId of epoch.creatures) {
                        if (!Core.Player.PlanetsSave.CurrentPlanet.collection.includes(cId)) {
                            Core.Player.PlanetsSave.CurrentPlanet.collection.push(cId);
                        }
                    }
                    if (!Core.Player.PlanetsSave.CurrentPlanet.epochs.includes(eId)) {
                        Core.Player.PlanetsSave.CurrentPlanet.epochs.push(eId);
                    }
                    if (epoch.id == 2) {
                        GameManager.gameController.AddCreatureRandomPosition(epoch.creatures[epoch.creatures.length - 1], false);
                        GameManager.gameController.AddCreatureRandomPosition(epoch.creatures[epoch.creatures.length - 1], false);
                    }
                }
                Core.Player.CallOnChanged();
                break;

            case RewardType.SpinsOffer1:
                Core.Player.MaxSpinsBonus = Core.GameData.Gameplay.spinsOfferBonus1;
                Core.Player.AddMoney(MoneyType.Spins, Core.Player.MaxSpinsNumber);
                Core.Player.SpinsOfferType = SpinOfferType.Second;
                Core.Player.SpinsUsed = 0;
                break;

            case RewardType.SpinsOffer2:
                Core.Player.MaxSpinsBonus = Core.GameData.Gameplay.spinsOfferBonus1 + Core.GameData.Gameplay.spinsOfferBonus2;
                Core.Player.AddMoney(MoneyType.Spins, Core.Player.MaxSpinsNumber);
                Core.Player.SpinsOfferType = SpinOfferType.Complete;
                Core.Player.SpinsUsed = 0;
                break;
        }
        return true;
    }
}

export class InGameProduct extends JSONGameData {

    Awake() {
        super.Awake();
        this.rewards = []; // Reward[]
        this.id = "";
        this.price = new Price();
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.price = new Price(this.price);
        this.rewards = this.rewards.map(data => new Reward(data));
    }

    toJSON() {
        return {
            rewards: this.rewards.map(obj => obj.toJSON()),
            id: this.id,
            price: this.price.toJSON(),
        }
    }
}

export class AppStoreProduct extends JSONGameData {

    get PlatformStoreId() {
        return "";
    }

    Awake() {
        super.Awake();
        this.rewards = []; // Reward[]
        this.id = "";
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.rewards = this.rewards.map(data => new Reward(data));
    }

    toJSON() {
        return {
            rewards: this.rewards.map(obj => obj.toJSON()),
            id: this.id,
        }
    }
}

export class GameplayData extends JSONGameData {

    Awake() {
        super.Awake();
        this.creaturePurchaseDistance = 0;
        this.adsCreaturePurchasesPerSession = 0;
        this.boostersCreatureLock = 0;
        this.mapCreatureLock = 0;
        this.incomeX2Seconds = 0;
        this.incomeX4Seconds = 0;
        this.spinsOfferTimeMinutes = 0;
        this.spinsOfferBonus1 = 0;
        this.spinsOfferBonus2 = 0;
        this.spinsOfferThreshold1 = 0;
        this.spinsOfferThreshold2 = 0;
        this.spinsOfferThreshold3 = 0;
    }

    toJSON() {
        return {
            creaturePurchaseDistance: this.creaturePurchaseDistance,
            adsCreaturePurchasesPerSession: this.adsCreaturePurchasesPerSession,
            boostersCreatureLock: this.boostersCreatureLock,
            mapCreatureLock: this.mapCreatureLock,
            incomeX2Seconds: this.incomeX2Seconds,
            incomeX4Seconds: this.incomeX4Seconds,
            spinsOfferTimeMinutes: this.spinsOfferTimeMinutes,
            spinsOfferBonus1: this.spinsOfferBonus1,
            spinsOfferBonus2: this.spinsOfferBonus2,
            spinsOfferThreshold1: this.spinsOfferThreshold1,
            spinsOfferThreshold2: this.spinsOfferThreshold2,
            spinsOfferThreshold3: this.spinsOfferThreshold3,
        }
    }
}

export class MissionsData extends JSONGameData {

    Awake() {
        super.Awake();
        this.missions = []; // MissionData[]
        this.missionsDic = new Map(); // Dictionary<int, MissionData>
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.missions = this.missions.map(data => new MissionData(data));
        for (let m of this.missions) {
            this.missionsDic.set(m.id, m);
        }
    }

    toJSON() {
        return {
            missions: this.missions.map(obj => obj.toJSON())
        }
    }

    GetMission(id) {
        return this.missionsDic.get(id);
    }
}

export class MissionData extends JSONGameData {

    get MissionSprite() {
        if (!this.sprite) {
            // this.sprite = Resources.Load<Sprite>("MissionIcons/default");
        }
        return this.sprite;
    }

    Awake() {
        super.Awake();
        this.id = 0;
        this.durationSeconds = 0;
        this.missionName = "";
        this.rewards = []; // Reward[]
        this.sprite = null; // Sprite
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.rewards = this.rewards.map(data => new Reward(data));
    }

    toJSON() {
        return {
            id: this.id,
            durationSeconds: this.durationSeconds,
            missionName: this.missionName,
            rewards: this.rewards.map(obj => obj.toJSON()),
        }
    }
}

export class ChestData extends JSONGameData {

    Awake() {
        super.Awake();
        this.rewards = []; // List<ChestItem>
        this.lastItem = null; // ChestItem
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.rewards = this.rewards.map(data => new ChestItem(data));
    }

    toJSON() {
        return {
            rewards: this.rewards.map(obj => obj.toJSON())
        }
    }

    Get(id) {
        for (let r of this.rewards) {
            if (r.id == id) return r;
        }
        return null;
    }

    GetRandomReverse() {
        return WeightedRandomization.ChooseReverse(this.rewards);
    }

    GetRandom() {
        let rand = WeightedRandomization.Choose(this.rewards);
        let tries = 0;
        while (rand == this.lastItem && tries < 5) {
            rand = WeightedRandomization.Choose(this.rewards);
            tries++;
        }
        this.lastItem = rand;
        return rand;
    }

    GetItemsByType(type) {
        let items = []; // new List<ChestItem>()
        for (let r of this.rewards) {
            if (r.type == type) items.push(r);
        }
        return items;
    }
}

export class ChestItem extends Reward {

    Awake() {
        super.Awake();
        this.id = 0;
        this.weight = 0;
        this.ReverseWeight = 0;
    }

    GetWeight() {
        return this.weight;
    }

    toJSON() {
        return {
            ...super.toJSON(),
            id: this.id,
            weight: this.weight,
        }
    }
}

export class SlotsData extends JSONGameData {

    Awake() {
        super.Awake();
        this.availableFromCollectionSize = 0;
        this.spinsMaxCount = 0;
        this.restorePeriodInSeconds = 0;
        this.restoreSpinsNumberByTime = 0;
        this.restoreSpinsNumberByAds = 0;
        this.purchaseSpinsPrice = new Price;
        this.purchaseSpinsNumber = 0;
        this.sets = []; // SlotSet[]
        this.comboRewards = []; // SlotItemReward[]
        this.noComboRewards = []; // SlotItemReward[]
        this.helpComboRewards = new Map(); // Dictionary<SlotItem, SlotItemReward>
        this.helpNoComboRewards = new Map(); // Dictionary<SlotItem, SlotItemReward>
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.purchaseSpinsPrice = new Price(this.purchaseSpinsPrice);
        this.sets = this.sets.map(data => new SlotSet(data));
        this.comboRewards = this.comboRewards.map(data => new SlotItemReward(data));
        this.noComboRewards = this.noComboRewards.map(data => new SlotItemReward(data));
        for (let c of this.comboRewards) {
            this.helpComboRewards.set(c.itemType, c);
        }
        for (let c of this.noComboRewards) {
            this.helpNoComboRewards.set(c.itemType, c);
        }
    }

    toJSON() {
        return {
            availableFromCollectionSize: this.availableFromCollectionSize,
            spinsMaxCount: this.spinsMaxCount,
            restorePeriodInSeconds: this.restorePeriodInSeconds,
            restoreSpinsNumberByTime: this.restoreSpinsNumberByTime,
            restoreSpinsNumberByAds: this.restoreSpinsNumberByAds,
            purchaseSpinsPrice: this.purchaseSpinsPrice.toJSON(),
            purchaseSpinsNumber: this.purchaseSpinsNumber,
            sets: this.sets.map(obj => obj.toJSON()),
            comboRewards: this.comboRewards.map(obj => obj.toJSON()),
            noComboRewards: this.noComboRewards.map(obj => obj.toJSON()),
        }
    }

    GetComboRewardForItem(type) {
        return this.helpComboRewards.get(type);
    }

    GetNoComboRewardForItem(type) {
        return this.helpNoComboRewards.get(type);
    }

    GetNoComboRewardForItems(items) {
        let totalMoney = 0n;
        for (let item of items) {
            let reward = Core.GameData.Slots.GetNoComboRewardForItem(item);
            if (reward && (reward.itemType == SlotItem.MoneySoft1 || reward.itemType == SlotItem.MoneySoft2)) {
                totalMoney += reward.reward.value;
            }
        }

        if (totalMoney > 0n) {
            return new SlotItemReward({
                itemType: SlotItem.MoneySoft1,
                reward: {
                    type: RewardType.MoneySoftBySoftSpeed,
                    valueStr: totalMoney.toString(),
                }
            });
        }
    }

    GetSet(spinsNumber) {
        let set = this.sets.find(s => (s.conditions.spinsMin <= spinsNumber && s.conditions.spinsMax >= spinsNumber));
        return set || this.sets[this.sets.length - 1];
    }

    GetMainRandom(spinsNumber) {
        let set = this.GetSet(spinsNumber);
        return WeightedRandomization.Choose(set.mainRandom);
    }

    GetNoComboRandom(spinsNumber) {
        let set = this.GetSet(spinsNumber);
        return WeightedRandomization.Choose(set.nocomboRandom);
    }

    GetNoComboRandomExcept(spinsNumber, except) {
        let set = this.GetSet(spinsNumber);
        let chooseList = set.nocomboRandom.filter(item => item.itemType !== except);
        return WeightedRandomization.Choose(chooseList);
    }

    GetTeaseringItem(spinsNumber) {
        let set = this.GetSet(spinsNumber);
        return WeightedRandomization.Choose(set.teaseringRandom);
    }
}

export class SlotItemReward extends JSONGameData {

    Awake() {
        super.Awake();
        this.itemType = ""; // SlotItem
        this.reward = new Reward();
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.reward = new Reward(this.reward);
    }

    toJSON() {
        return {
            itemType: this.itemType,
            reward: this.reward.toJSON(),
        }
    }
}

export class SlotSet extends JSONGameData {

    Awake() {
        super.Awake();
        this.conditions = new SlotSetConditions();
        this.mainRandom = []; // List<SlotSetWeightedItem>
        this.nocomboRandom = []; // List<SlotSetWeightedItem>
        this.teaseringRandom = []; // List<SlotSetWeightedItem>
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.conditions = new SlotSetConditions(this.conditions);
        this.mainRandom = this.mainRandom.map(data => new SlotSetWeightedItem(data));
        this.nocomboRandom = this.nocomboRandom.map(data => new SlotSetWeightedItem(data));
        this.teaseringRandom = this.teaseringRandom.map(data => new SlotSetWeightedItem(data));
    }

    toJSON() {
        return {
            conditions: this.conditions.toJSON(),
            mainRandom: this.mainRandom.map(obj => obj.toJSON()),
            nocomboRandom: this.nocomboRandom.map(obj => obj.toJSON()),
            teaseringRandom: this.teaseringRandom.map(obj => obj.toJSON()),
        }
    }
}

export class SlotSetWeightedItem extends JSONGameData {

    get HasCombo() {
        return this.hasCombo == 1;
    }

    get Teasering() {
        return this.teasering == 1;
    }

    Awake() {
        super.Awake();
        this.teasering = 0;
        this.hasCombo = 0;
        this.itemType = ""; // SlotItem
        this.weight = 0;
        this.ReverseWeight = 0;
    }

    toJSON() {
        return {
            teasering: this.teasering,
            hasCombo: this.hasCombo,
            itemType: this.itemType,
            weight: this.weight,
        }
    }

    GetWeight() {
        return this.weight;
    }
}

export class SlotSetConditions extends JSONGameData {

    Awake() {
        super.Awake();
        this.spinsMin = 0;
        this.spinsMax = 0;
    }

    toJSON() {
        return {
            spinsMin: this.spinsMin,
            spinsMax: this.spinsMax,
        }
    }
}

export class InterstitialRewards extends JSONGameData {

    Awake() {
        super.Awake();
        this.rewardsX2Unactive = []; // List<InterstitialReward>
        this.rewardsX2Active = []; // List<InterstitialReward>
        this.rewardsForChoose = []; // List<InterstitialReward>
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.rewardsX2Unactive = this.rewardsX2Unactive.map(data => new InterstitialReward(data));
        this.rewardsX2Active = this.rewardsX2Active.map(data => new InterstitialReward(data));
        this.rewardsForChoose = this.rewardsForChoose.map(data => new InterstitialReward(data));
    }

    toJSON() {
        return {
            rewardsX2Unactive: this.rewardsX2Unactive.map(obj => obj.toJSON()),
            rewardsX2Active: this.rewardsX2Active.map(obj => obj.toJSON()),
            rewardsForChoose: this.rewardsForChoose.map(obj => obj.toJSON()),
        }
    }
}

export class InterstitialReward extends Reward {

    Awake() {
        super.Awake();
        this.id = 0;
        this.weight = 0;
        this.ReverseWeight = 0;
    }

    toJSON() {
        return {
            ...super.toJSON(),
            id: this.id,
            weight: this.weight,
        }
    }

    GetWeight() {
        return this.weight;
    }
}

export class WelcomeBackSettings extends JSONGameData {

    Awake() {
        super.Awake();
        this.MinSecondsToFire = 0; // double
        this.MoneyBoxMultiplayer = 0; // double
    }

    toJSON() {
        return {
            MinSecondsToFire: this.MinSecondsToFire,
            MoneyBoxMultiplayer: this.MoneyBoxMultiplayer,
        }
    }
}
